/**
 * R2.5's shared display helpers — the one util module the settled-row stats
 * needed. `formatDate` moved here verbatim from GraveyardView so TaskLine's
 * hover summary reuses the same face instead of duplicating it.
 */
import type { Task } from '../types/generated';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** A stored millisecond timestamp as `Nov 14, 2023`; empty for null. */
export function formatDate(ms: number | null): string {
  if (ms == null) return '';
  const date = new Date(ms);
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/** An ISO logical day (`YYYY-MM-DD`) in the same face, year always shown. */
export function formatDay(day: string): string {
  const [year, month, day_] = day.split('-');
  return `${MONTHS[Number(month) - 1] ?? ''} ${Number(day_)}, ${year}`;
}

/**
 * How many re-entries produced a task: the depth of its `parent_task_id`
 * lineage — the GraveyardView pattern (a parent map plus a cursor walk),
 * counted upward. An original entry is 0; a parent outside `tasks` still
 * counts one.
 */
export function reentryDepth(tasks: Task[], taskId: string): number {
  const parentOf = new Map(
    tasks
      .filter((t) => t.parent_task_id)
      .map((t) => [t.task_id, t.parent_task_id!]),
  );
  let depth = 0;
  let cursor =
    tasks.find((t) => t.task_id === taskId)?.parent_task_id ?? null;
  while (cursor) {
    depth += 1;
    cursor = parentOf.get(cursor) ?? null;
  }
  return depth;
}

/** A URL found in a title, with the text around it verbatim. */
export interface TitleLink {
  before: string;
  /** The URL itself — the href and the hover tooltip both. */
  url: string;
  after: string;
}

/**
 * R5.6's split: the first `http://`/`https://` substring running to the next
 * whitespace or the end — SCHEME-ONLY, so a bare `www.` address is prose. The
 * underlying op data never changes; both title surfaces split at render time.
 */
export function splitTitleLink(title: string): TitleLink | null {
  const match = /https?:\/\/\S*/.exec(title);
  if (match?.[0] === undefined) return null;
  const url = match[0];
  return {
    before: title.slice(0, match.index),
    url,
    after: title.slice(match.index + url.length),
  };
}
